import React, { FormEvent, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { isEmail } from 'validator';
import { v4 as uuidv4 } from 'uuid';

import { optionsHelper, dateHelper, config } from 'modules/common/helpers';

import { Col, Row } from 'modules/bootstrap/components';
import {
  TextInputRef,
  SelectController,
  BirthdayInput,
  CountryInput,
  PhoneInput,
  Button,
} from 'modules/common/components';

type Inputs = {
  id?: string;
  gender: SelectOption;
  name: string;
  surname: string;
  birthday: string;
  placeOfBirth: string;
  zip: string;
  address: string;
  city: string;
  country: SelectOption;
  email: string;
  phone: string;
};

export interface HeirContactFormProps {
  contactItem?: ContactDTO;
  onCancel: () => void;
  onSuccess: (isNewHeir: boolean, submitData) => void;
}

function HeirContactForm(props: HeirContactFormProps) {
  const { onCancel, onSuccess } = props;
  const contactItem = (props.contactItem || {}) as ContactDTO;

  const { t } = useTranslation(['auth', 'common', 'contacts']);
  const {
    register,
    handleSubmit,
    control,
    getValues,
    setValue,

    formState: { errors },
  } = useForm<Inputs>();
  const [loading, setLoading] = React.useState(false);

  const isNewContact = !contactItem.id;

  const onClose = useCallback(() => {
    onCancel();
  }, [onCancel]);

  const onSubmit = useCallback(
    async (formData: Inputs) => {
      setLoading(true);
      const submitData = {
        ...formData,
        id: contactItem.id || uuidv4(),
        gender: (formData?.gender?.value as string) || '',
        country: (formData.country && (formData.country.value as string)) || '',
        birthday:
          formData.birthday &&
          dateHelper.convertDateFormat(
            formData.birthday,
            config.format.serverDate
          ),
      } as ContactDTO;

      onSuccess(isNewContact, submitData);
    },
    [onClose, onSuccess]
  );

  const submit = (e: FormEvent) => {
    e.stopPropagation();
    e.preventDefault();
    handleSubmit(() => {
      onSubmit(getValues());
    })();
  };

  const textFieldRequired = t('common:field_required');
  const textFieldNotValid = t('common:field_not_valid');
  const genderOptions = optionsHelper.getGenderOptions();
  const defaultGenderOptions =
    genderOptions.find((item) => item.value === contactItem.gender) ||
    genderOptions[0];

  return (
    <div data-testid="HeirContactForm">
      <form onSubmit={submit} className="Form" data-testid="heir-form">
        {!isNewContact && (
          <input
            type="hidden"
            id="id"
            {...register('id')}
            value={contactItem.id}
          />
        )}

        <Row>
          <Col xl={{ span: 4 }}>
            <div className="Form-group">
              <label className="Form-label" htmlFor="gender">
                {t('auth:title')}
              </label>

              <SelectController
                id="gender"
                name="gender"
                options={genderOptions}
                control={control}
                defaultValue={defaultGenderOptions}
              />
            </div>
          </Col>
        </Row>

        <Row>
          <Col xl={{ span: 4 }}>
            <TextInputRef
              {...register('name', { required: true })}
              label={t('auth:name_f')}
              type="text"
              autoComplete={false}
              defaultValue={contactItem.name}
              error={errors.name && textFieldRequired}
            />
          </Col>

          <Col xl={{ span: 8 }}>
            <TextInputRef
              {...register('surname', { required: true })}
              label={t('auth:name_l')}
              type="text"
              autoComplete={false}
              defaultValue={contactItem.surname}
              error={errors.surname && textFieldRequired}
            />
          </Col>
        </Row>

        <Row>
          <Col xl={{ span: 4 }}>
            <BirthdayInput
              control={control}
              error={errors?.birthday?.message}
              defaultValue={dateHelper.convertDateFormat(
                contactItem.birthday || '',
                config.format.uiDate
              )}
              isRequired={true}
            />
          </Col>

          <Col xl={{ span: 8 }}>
            <TextInputRef
              {...register('placeOfBirth', { required: true })}
              label={t('auth:birthplace_label')}
              type="text"
              defaultValue={contactItem.placeOfBirth}
              error={errors.placeOfBirth && textFieldRequired}
            />
          </Col>
        </Row>

        <Row>
          <Col xl={{ span: 8 }}>
            <CountryInput
              control={control}
              error={errors.country}
              defaultValue={contactItem.country}
              isRequired={true}
              refSetValue={setValue}
              refGetValues={getValues}
            />
          </Col>
        </Row>

        <Row>
          <Col xl={{ span: 4 }}>
            <TextInputRef
              {...register('zip', { required: true })}
              label={t('auth:postal_label')}
              type="text"
              error={errors.zip && textFieldRequired}
              defaultValue={contactItem.zip}
            />
          </Col>

          <Col xl={{ span: 8 }}>
            <TextInputRef
              {...register('address', { required: true })}
              label={t('auth:address_label')}
              type="text"
              error={errors.address && textFieldRequired}
              defaultValue={contactItem.address}
            />
          </Col>
        </Row>

        <Row>
          <Col xl={{ span: 8 }}>
            <TextInputRef
              {...register('city', { required: true })}
              label={t('auth:city_label')}
              type="text"
              error={errors.city && textFieldRequired}
              defaultValue={contactItem.city}
            />
          </Col>
        </Row>

        <Row>
          <Col xl={{ span: 12 }}>
            <TextInputRef
              {...register('email', {
                required: false,
                validate: (value) => {
                  if (!value) return true;

                  return isEmail(value);
                },
              })}
              label={t('auth:email_label')}
              type="text"
              error={errors.email && textFieldNotValid}
              defaultValue={contactItem.email}
              onBlur={(e: React.ChangeEvent<HTMLInputElement>) =>
                setValue('email', e.target.value.trim())
              }
            />
          </Col>
        </Row>

        <Row>
          <Col>
            <PhoneInput
              control={control}
              error={errors.phone && textFieldNotValid}
              defaultValue={contactItem.phone}
              isRequired={false}
            />
          </Col>
        </Row>

        <div className="t-flex t-flex-wrap t-justify-end">
          <Button
            category="secondary"
            className="t-whitespace-nowrap t-self-end"
            onClick={onClose}
            disabled={loading}
          >
            {t('common:cancel')}
          </Button>
          <Button type="submit" className="t-ml-5 xs:t-mt-5" loading={loading}>
            {t('common:save_and_continue')}
          </Button>
        </div>
      </form>
    </div>
  );
}

export default HeirContactForm;
